import {
  graphemes,
  measureIosText,
} from '@/lib/ios-notification/ios-notification-layout';

/**
 * App name under a home screen icon of an iPhone 17 (402pt) on iOS 26, in
 * points, as measured on the iOS 26.5 simulator with test apps of chosen
 * names (see ios-app-label-layout.spec.ts). SF Pro 12pt regular, one line;
 * SpringBoard never shrinks the font, it tightens the letter spacing:
 *
 * 1. as is, when it fits `maxWidth`;
 * 2. tracking up to `spacedTracking` between every two glyphs;
 * 3. spaces narrowed to `narrowSpace` and tracking up to `tightTracking`
 *    (the name reads as if its spaces were gone: "AteliêAuroraModa");
 * 4. otherwise cut with "…" at the longest start that fits with narrowed
 *    spaces and `truncationTracking`, drawn with that tracking.
 *
 * Tracking in 2 and 3 is only what the name needs to fill `maxWidth`.
 */
export const IOS_APP_LABEL = {
  fontSize: 12,
  maxWidth: 89.44,
  spacedTracking: 0.805,
  tightTracking: 0.891,
  truncationTracking: 0.818,
  narrowSpace: 1.25,
} as const;

export interface IosAppLabel {
  /** Text under the icon, spaces included (narrowed when `narrowSpaces`). */
  text: string;
  /** Letter spacing to remove between glyphs, in points (>= 0). */
  tracking: number;
  /** Spaces are drawn `IOS_APP_LABEL.narrowSpace` wide. */
  narrowSpaces: boolean;
}

const ELLIPSIS = '…';
/** Width of a regular space in the label font. */
export const IOS_APP_LABEL_SPACE_WIDTH = measureIosText(' ', 'label');

/** Width of `text` with `tracking` between glyphs and, if asked, narrow spaces. */
export function measureIosAppLabel(
  text: string,
  tracking: number,
  narrowSpaces: boolean
): number {
  const gs = graphemes(text);
  const spaces = gs.filter((g) => g === ' ').length;
  const narrowing = narrowSpaces
    ? spaces * (IOS_APP_LABEL_SPACE_WIDTH - IOS_APP_LABEL.narrowSpace)
    : 0;
  return (
    measureIosText(text, 'label') -
    tracking * Math.max(gs.length - 1, 0) -
    narrowing
  );
}

/** Tracking that makes `text` exactly `maxWidth` wide (0 when it fits). */
function neededTracking(text: string, narrowSpaces: boolean): number {
  const glyphs = graphemes(text).length;
  const width = measureIosAppLabel(text, 0, narrowSpaces);
  if (width <= IOS_APP_LABEL.maxWidth || glyphs < 2) return 0;
  return (width - IOS_APP_LABEL.maxWidth) / (glyphs - 1);
}

/** The app name the way the iOS 26 home screen draws it under the icon. */
export function layoutIosAppLabel(name: string): IosAppLabel {
  const { spacedTracking, tightTracking, truncationTracking, maxWidth } =
    IOS_APP_LABEL;
  const text = name.normalize('NFC').replace(/\s+/gu, ' ').trim();

  const spaced = neededTracking(text, false);
  if (spaced <= spacedTracking) {
    return { text, tracking: spaced, narrowSpaces: false };
  }
  const tight = neededTracking(text, true);
  if (tight <= tightTracking) {
    return { text, tracking: tight, narrowSpaces: true };
  }

  const gs = graphemes(text);
  let cut = ELLIPSIS;
  for (let n = 1; n <= gs.length; n++) {
    const candidate = gs.slice(0, n).join('').trimEnd() + ELLIPSIS;
    if (measureIosAppLabel(candidate, truncationTracking, true) > maxWidth) {
      break;
    }
    cut = candidate;
  }
  return { text: cut, tracking: truncationTracking, narrowSpaces: true };
}
